import PropTypes from 'prop-types';
import styled from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import { PaneInner, SearchInputContainer, StyledPane } from '../common/styles';
import paneId from './paneId';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useExternalMedia } from '@src/web-stories-wp/story-editor/src/app/externalMedia';
import ExternalMediaContent from './externalMediaContent';
import { Media3pPane } from '../media3p';
import { useMedia } from '@src/web-stories-wp/story-editor/src/app';
import { useMediaProvidersOptions } from '@src/web-stories-wp/story-editor/src/app/media';
import { is3pMediaProvider } from '../../../../../app/media/media3p/providerConfiguration';
import OptionsList from '@src/components/optionsList/optionsList';
import OptionsListBackButton from '@src/components/optionsList/optionsListBackButton/optionsListBackButton';
import { SearchInput } from '../../../common';
import { Icons } from '@web-stories-wp/design-system';

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 0 0;
`;

const OptionsListWrapper = styled.div`
  align-items: center;
  background-color: transparent;
  border: none;
  color: white;
  display: flex;
  font-size: 16px;
  justify-content: space-between;
  text-decoration: none;
  transition: padding 0.3s ease-in-out;
  width: 100%;
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  color: #919899;
  cursor: pointer;
  padding: 0;
  width: var(--icon-button-size);
  height: var(--icon-button-size);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RefreshImage = styled(Icons.Refresh)`
  width: 20px;
  height: 20px;
`;

function ExternalMediaPane(props) {
  const [folderBreadcrumb, setFolderBreadcrumb] = useState(null);

  const mediaProvidersOptions = useMediaProvidersOptions();
  const areOptionsAvailable = mediaProvidersOptions.length > 0;

  const {
    actions: { updateMediaType, refreshMedia, updateFilters, updateSearchTerm },
    state: { filters, mediaType, searchTerm },
  } = useExternalMedia();

  const {
    setSearchTerm: setMedia3pSearchTerm,
    selectedProvider,
    setSelectedProvider,
    resetMedia,
  } = useMedia(
    ({
      media3p: {
        state: { selectedProvider },
        actions: { setSearchTerm, setSelectedProvider, resetMedia },
      },
    }) => ({
      selectedProvider,
      setSearchTerm,
      setSelectedProvider,
      resetMedia,
    })
  );

  const isMediaTypeSelected = useMemo(
    () => Object.keys(mediaType).length > 0,
    [mediaType]
  );

  const is3pMedia = is3pMediaProvider(mediaType.value);

  const selectMediaProvider = useCallback(
    (mediaProviderId) => {
      const selectedOption = mediaProvidersOptions.find(
        (option) => option.value.mediaProviderId === mediaProviderId
      );

      if (!selectedOption) {
        return;
      }

      const providerKey = selectedOption.value.providerConfigurationKey;
      const isThirdParty = is3pMediaProvider(providerKey);

      // Reset media if we select same media provider type but different instance
      // to make sure previous media is not displayed before fetch start
      if (
        selectedProvider?.providerConfigurationKey === providerKey &&
        selectedProvider?.mediaProviderId !==
          selectedOption.value.mediaProviderId
      ) {
        resetMedia({ provider: selectedProvider?.providerConfigurationKey });
      }

      updateMediaType({
        label: selectedOption.label,
        value: providerKey,
      });

      if (isThirdParty) {
        const previousProviderKey = selectedProvider?.providerConfigurationKey;
        if (previousProviderKey && previousProviderKey !== providerKey) {
          setMedia3pSearchTerm({
            folder: '',
            searchTerm: '',
            tag: '',
            player: '',
          });
        }
        setSelectedProvider(selectedOption.value);
      } else {
        setFolderBreadcrumb(null);
        setSelectedProvider(null);
        setMedia3pSearchTerm({
          folder: '',
          searchTerm: '',
          tag: '',
          player: '',
        });
      }
    },
    [
      mediaProvidersOptions,
      updateMediaType,
      selectedProvider,
      setSelectedProvider,
      resetMedia,
      setMedia3pSearchTerm,
    ]
  );

  const showExternalMediaContent = isMediaTypeSelected && !is3pMedia;
  const showMedia3Pane = isMediaTypeSelected && is3pMedia;

  useEffect(() => {
    if (!showMedia3Pane) {
      setFolderBreadcrumb(null);
    }
  }, [showMedia3Pane]);

  const deselectMediaProviderOrFolder = useCallback(() => {
    // Handle folder navigation (for providers with usesFolderAsQueryParam)
    if (folderBreadcrumb?.onClick) {
      folderBreadcrumb.onClick();
      return;
    }

    if (!!filters.folderName) {
      updateFilters({ ...filters, searchText: '', folderName: '' });

      if (showExternalMediaContent) {
        return;
      }
    }

    updateMediaType({});
  }, [
    folderBreadcrumb,
    filters.folderName,
    showExternalMediaContent,
    updateFilters,
    updateMediaType,
  ]);

  const mediaProvidersOptionsReadyForOptionsList = useMemo(
    () =>
      mediaProvidersOptions
        .map((option) => ({
          label: option.label,
          key: option.value.mediaProviderId,
        }))
        .filter((option) =>
          searchTerm
            ? option.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
            : true
        ),
    [mediaProvidersOptions, searchTerm]
  );

  const filterMediaProviders = useCallback(
    (searchTerm) => {
      updateSearchTerm(searchTerm);
    },
    [updateSearchTerm]
  );

  return (
    <StyledPane id={paneId} {...props}>
      <PaneInner>
        {!isMediaTypeSelected && (
          <>
            <FiltersContainer>
              <SearchInputContainer>
                <SearchInput
                  initialValue={searchTerm}
                  placeholder={__('Filter', 'web-stories')}
                  onSearch={filterMediaProviders}
                  incremental
                  disabled={mediaProvidersOptions.length === 0}
                  type="filter"
                />
              </SearchInputContainer>
            </FiltersContainer>

            <OptionsList
              options={mediaProvidersOptionsReadyForOptionsList}
              shouldDisplayOptionsList={areOptionsAvailable}
              setChosenOption={selectMediaProvider}
              disableAllOptions
              shouldDisplayNoOptionFoundMessage={
                mediaProvidersOptionsReadyForOptionsList.length === 0
              }
            />
          </>
        )}
        {isMediaTypeSelected && (
          <>
            <OptionsListWrapper>
              <OptionsListBackButton
                className="templatesListByFolder__goBack"
                onClick={deselectMediaProviderOrFolder}
              >
                {folderBreadcrumb?.text ||
                  mediaType.label +
                    (filters.folderName ? ` | ${filters.folderName?.value}` : '')}
              </OptionsListBackButton>
              <RefreshButton
                onClick={() => {
                  if (!!showMedia3Pane) {
                    resetMedia({
                      provider: selectedProvider?.providerConfigurationKey,
                    });
                    setMedia3pSearchTerm({
                      folder: '',
                      searchTerm: '',
                      tag: '',
                    });
                  } else {
                    refreshMedia();
                  }
                }}
              >
                <RefreshImage />
              </RefreshButton>
            </OptionsListWrapper>
            <ExternalMediaContent
              isActive={areOptionsAvailable && showExternalMediaContent}
            />
            <Media3pPane
              isTabListEnabled={false}
              selectedTab={mediaType.value}
              isActive={areOptionsAvailable && showMedia3Pane}
              onBreadcrumbChange={setFolderBreadcrumb}
            />
          </>
        )}
      </PaneInner>
    </StyledPane>
  );
}

ExternalMediaPane.propTypes = {
  isActive: PropTypes.bool,
};

export default ExternalMediaPane;
